/**
 * * Labels for a resource association.
 * * Kept in step with ATF_Core/src/services/catalogResourceRules.ts.
 * * Write includes read. A broader grant hides the targets it already covers.
 */

export interface BindingGrant {
  scope: 'global' | 'circle' | 'role'
  level: 'read' | 'write'
  circleId?: string | null
  roleId?: string | null
}

export interface NamedRef {
  id: string
  name: string
  circleId?: string
}

export interface VisibilityAudience {
  everyone: boolean
  circles: string[]
  roles: string[]
}

export interface VisibilitySummary {
  read: VisibilityAudience | null
  write: VisibilityAudience | null
}

export function grantCoversRole(grant: BindingGrant, roleId: string, circleId: string): boolean {
  if (grant.scope === 'global') {
    return true
  }
  if (grant.scope === 'circle') {
    return grant.circleId === circleId
  }
  return grant.roleId === roleId
}

/**
 * * Keeps only the grants a reader cannot infer.
 * * A team-wide grant hides circles and roles. A circle grant hides that circle's roles.
 */
export function summarizeVisibility(grants: BindingGrant[], circles: NamedRef[], roles: NamedRef[]): VisibilitySummary {
  const indexed = indexGrants(grants)
  const writeCircleIds = idsAt(indexed.circles, 'write')
  const readCircleIds = idsAt(indexed.circles, 'read')
  const writeRoles = rolesOutside(indexed.roles, 'write', roles, writeCircleIds)
  const readRoles = rolesOutside(indexed.roles, 'read', roles, new Set([...writeCircleIds, ...readCircleIds]))
  if (indexed.global === 'write') {
    return { read: null, write: { everyone: true, circles: [], roles: [] } }
  }
  const write = audience(false, namesFor(writeCircleIds, circles), namesFor(writeRoles, roles))
  if (indexed.global === 'read') {
    return { read: { everyone: true, circles: [], roles: [] }, write }
  }
  return {
    read: audience(false, namesFor(readCircleIds, circles), namesFor(readRoles, roles)),
    write,
  }
}

export function formatVisibility(grants: BindingGrant[], circles: NamedRef[], roles: NamedRef[]): string {
  return formatSummary(summarizeVisibility(grants, circles, roles))
}

export function formatSummary(summary: VisibilitySummary): string {
  const lines: string[] = []
  if (summary.read) {
    lines.push(`Lecture — ${audiencePhrase(summary.read)}`)
  }
  if (summary.write) {
    lines.push(`Écriture — ${audiencePhrase(summary.write)}`)
  }
  if (lines.length === 0) {
    return 'Aucun droit'
  }
  return lines.join('\n')
}

export function audiencePhrase(audience: VisibilityAudience): string {
  if (audience.everyone) {
    return "toute l'équipe"
  }
  const parts: string[] = []
  if (audience.circles.length === 1) {
    parts.push(`cercle ${audience.circles[0]}`)
  } else if (audience.circles.length > 1) {
    parts.push(`cercles ${audience.circles.join(', ')}`)
  }
  if (audience.roles.length === 1) {
    parts.push(`rôle ${audience.roles[0]}`)
  } else if (audience.roles.length > 1) {
    parts.push(`rôles ${audience.roles.join(', ')}`)
  }
  return parts.join(' · ')
}

function indexGrants(grants: BindingGrant[]): {
  global: 'read' | 'write' | null
  circles: Map<string, 'read' | 'write'>
  roles: Map<string, 'read' | 'write'>
} {
  let global: 'read' | 'write' | null = null
  const circles = new Map<string, 'read' | 'write'>()
  const roles = new Map<string, 'read' | 'write'>()
  for (const grant of grants) {
    if (grant.scope === 'global') {
      global = stronger(global, grant.level)
      continue
    }
    if (grant.scope === 'circle' && grant.circleId) {
      circles.set(grant.circleId, stronger(circles.get(grant.circleId) ?? null, grant.level))
      continue
    }
    if (grant.scope === 'role' && grant.roleId) {
      roles.set(grant.roleId, stronger(roles.get(grant.roleId) ?? null, grant.level))
    }
  }
  return { global, circles, roles }
}

function stronger(left: 'read' | 'write' | null, right: 'read' | 'write'): 'read' | 'write' {
  if (left === 'write' || right === 'write') {
    return 'write'
  }
  return 'read'
}

function idsAt(levels: Map<string, 'read' | 'write'>, level: 'read' | 'write'): Set<string> {
  return new Set([...levels.entries()].filter((entry) => entry[1] === level).map((entry) => entry[0]))
}

function rolesOutside(
  levels: Map<string, 'read' | 'write'>,
  level: 'read' | 'write',
  roles: NamedRef[],
  coveredCircles: Set<string>,
): Set<string> {
  const kept = new Set<string>()
  for (const [roleId, roleLevel] of levels) {
    if (roleLevel !== level) {
      continue
    }
    const circleId = roles.find((role) => role.id === roleId)?.circleId
    if (circleId && coveredCircles.has(circleId)) {
      continue
    }
    kept.add(roleId)
  }
  return kept
}

function namesFor(ids: Set<string>, refs: NamedRef[]): string[] {
  const names: string[] = []
  const seen = new Set<string>()
  for (const ref of refs) {
    if (!ids.has(ref.id) || seen.has(ref.id)) {
      continue
    }
    seen.add(ref.id)
    names.push(ref.name)
  }
  for (const id of ids) {
    if (!seen.has(id)) {
      names.push(id)
    }
  }
  return names
}

function audience(everyone: boolean, circles: string[], roles: string[]): VisibilityAudience | null {
  if (!everyone && circles.length === 0 && roles.length === 0) {
    return null
  }
  return { everyone, circles, roles }
}
